/**
 * Every date on screen, rendered in one place.
 *
 * 🔴 WHY ONE PLACE. `users.date_format` has been settable since the Preferences
 * form shipped and read by nothing, because there was nowhere to read it: dates
 * were rendered three different ways in ~160 places — `formatDate(v, 'dd MMM
 * yyyy')` with the pattern spelled inline, `toLocaleDateString()`, and bare
 * date-fns `format()`. A preference cannot be honoured by 160 call sites
 * agreeing to check it; it is honoured by there being one renderer.
 *
 * 🔴 THE PATTERN IS THE USER'S, THE ZONE IS THE ORGANISATION'S. These are
 * different questions and were being answered by the same omission. Which day
 * a timestamp falls on belongs to the BUILDING — a 7pm IST booking is the 22nd
 * in Mumbai and the 21st in London, and the server already judges it in the
 * organisation's zone. How that day is spelled belongs to the PERSON reading
 * it. So the zone comes from orgTimeZone() and the order from the account,
 * and neither is left to the machine the page happens to be open on.
 *
 * `users.timezone` is NOT read here. It is stored, settable and honoured by
 * nothing, and the Settings page says so with the control disabled — wiring it
 * in silently would make that note a lie.
 *
 * WHAT ISO MEANS HERE. A person who picks YYYY-MM-DD has asked for ISO, so
 * they get ISO everywhere, including where the other two patterns show a month
 * name. The alternative — "2026 Sep 22" — is a shape no standard defines and
 * nobody asked for.
 */
import { useAuthStore } from '@/stores/authStore';

import { orgTimeZone } from './timezone';

export type DatePattern = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/** The three the Preferences form offers, in the order it offers them. */
export const DATE_PATTERNS: readonly DatePattern[] = [
  'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD',
];

/**
 * What an account with NO preference sees.
 *
 * A display fallback, never a stored value: the form sends only the fields a
 * person changed, so showing this cannot write it. 139 of the 141 accounts on
 * the deployment hold NULL, and that is the honest state — "nobody has said".
 */
export const DEFAULT_DATE_PATTERN: DatePattern = 'DD/MM/YYYY';

export function isDatePattern(v: unknown): v is DatePattern {
  return typeof v === 'string' && (DATE_PATTERNS as readonly string[]).includes(v);
}

/**
 * The signed-in account's pattern, or the default.
 *
 * Read from the store on every call rather than captured: changing the
 * preference updates /auth/me, and the next render must pick it up without a
 * reload. The store read is a plain property access.
 */
export function userDatePattern(): DatePattern {
  const v = useAuthStore.getState().user?.date_format;
  return isDatePattern(v) ? v : DEFAULT_DATE_PATTERN;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

export type DateValue = string | number | Date | null | undefined;

export interface DisplayOptions {
  /** What a null, empty or unparseable value renders as. */
  fallback?: string;
  /**
   * Override the zone. Passed only by the screens that belong to a BUILDING
   * rather than to a viewer — the lobby signage and the room panel — which
   * must show the building's clock whatever machine is driving them.
   */
  timeZone?: string;
  /** Force a pattern instead of the account's. Used by the preview on the
   *  Preferences form, which has to show a pattern nobody has saved yet. */
  pattern?: DatePattern;
  /** Show seconds. Only the kiosk clock wants them — a ticking second hand is
   *  the point of that screen and noise on every other. */
  seconds?: boolean;
}

const LOCALE = 'en-GB';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const FULL_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday'];

function asDate(value: DateValue): Date | null {
  if (value === null || value === undefined || value === '') return null;
  // A bare 'YYYY-MM-DD' is parsed by the Date constructor as UTC MIDNIGHT, so
  // reading its parts in a zone behind UTC lands on the previous day — the
  // off-by-one this whole module exists to stop. A date with no time is a
  // CALENDAR date and carries no zone, so it is read literally.
  if (typeof value === 'string') {
    const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (plain) {
      return new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]));
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whether this value carried a time at all. A calendar date is rendered in no
 *  zone; an instant is rendered in the organisation's. */
function isPlainDate(value: DateValue): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

interface Parts { y: number; m: number; d: number; weekday: number }

function partsIn(d: Date, zone: string | undefined): Parts {
  if (!zone) {
    return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(), weekday: d.getDay() };
  }
  try {
    const f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      weekday: 'short',
    });
    const p: Record<string, string> = {};
    for (const part of f.formatToParts(d)) p[part.type] = part.value;
    return {
      y: Number(p.year), m: Number(p.month), d: Number(p.day),
      weekday: Math.max(0, WEEKDAYS.indexOf(p.weekday ?? '')),
    };
  } catch {
    // An unknown zone must not break a table. The viewer's day is wrong less
    // often than a thrown RangeError is useful.
    return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate(), weekday: d.getDay() };
  }
}

function zoneFor(value: DateValue, o?: DisplayOptions): string | undefined {
  if (isPlainDate(value)) return undefined;   // a calendar date has no zone
  return o?.timeZone ?? orgTimeZone();
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The account's pattern, fully numeric. "22/09/2026", "09/22/2026",
 * "2026-09-22".
 *
 * This is the default renderer: where a date has no reason to be spelled any
 * other way, it is spelled this way.
 */
export function displayDate(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const { y, m, d: day } = partsIn(d, zoneFor(value, o));
  switch (o?.pattern ?? userDatePattern()) {
    case 'MM/DD/YYYY': return `${pad(m)}/${pad(day)}/${y}`;
    case 'YYYY-MM-DD': return `${y}-${pad(m)}-${pad(day)}`;
    default:           return `${pad(day)}/${pad(m)}/${y}`;
  }
}

/**
 * The same day with the month written out — "22 Sep 2026", "Sep 22, 2026" —
 * for the places that had a month name before this and should keep one.
 *
 * ISO stays ISO, as the note at the top says.
 */
export function displayDateLong(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const pattern = o?.pattern ?? userDatePattern();
  if (pattern === 'YYYY-MM-DD') return displayDate(value, o);
  const { y, m, d: day } = partsIn(d, zoneFor(value, o));
  const mon = MONTHS[m - 1];
  return pattern === 'MM/DD/YYYY' ? `${mon} ${day}, ${y}` : `${day} ${mon} ${y}`;
}

/** "Mon, 22 Sep" / "Mon, Sep 22" / "Mon, 2026-09-22" — a date in a list of
 *  days, where the year is the one thing the reader already knows. */
export function displayDayDate(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const pattern = o?.pattern ?? userDatePattern();
  const { m, d: day, weekday } = partsIn(d, zoneFor(value, o));
  const wd = WEEKDAYS[weekday];
  if (pattern === 'YYYY-MM-DD') return `${wd}, ${displayDate(value, o)}`;
  const mon = MONTHS[m - 1];
  return pattern === 'MM/DD/YYYY' ? `${wd}, ${mon} ${day}` : `${wd}, ${day} ${mon}`;
}

/** "22 Sep" / "Sep 22" / "2026-09-22" — a date inside a period the reader is
 *  already looking at, where the year would be noise. */
export function displayDayMonth(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const pattern = o?.pattern ?? userDatePattern();
  if (pattern === 'YYYY-MM-DD') return displayDate(value, o);
  const { m, d: day } = partsIn(d, zoneFor(value, o));
  const mon = MONTHS[m - 1];
  return pattern === 'MM/DD/YYYY' ? `${mon} ${day}` : `${day} ${mon}`;
}

/** "Monday, 22 September 2026" — the one-date-on-the-page shape, for a kiosk
 *  or a lobby screen where the date IS the content. */
export function displayFull(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const pattern = o?.pattern ?? userDatePattern();
  const { y, m, d: day, weekday } = partsIn(d, zoneFor(value, o));
  const wd = FULL_WEEKDAYS[weekday];
  if (pattern === 'YYYY-MM-DD') return `${wd}, ${displayDate(value, o)}`;
  const mon = FULL_MONTHS[m - 1];
  return pattern === 'MM/DD/YYYY'
    ? `${wd}, ${mon} ${day}, ${y}`
    : `${wd}, ${day} ${mon} ${y}`;
}

/** "Sep 2026". No day, so the pattern has nothing to order — here so that a
 *  month label needs no second renderer. */
export function displayMonthYear(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const { y, m } = partsIn(d, zoneFor(value, o));
  if ((o?.pattern ?? userDatePattern()) === 'YYYY-MM-DD') return `${y}-${pad(m)}`;
  return `${MONTHS[m - 1]} ${y}`;
}

/** "02:30 pm", in the organisation's zone unless one is given. */
export function displayTime(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const zone = o?.timeZone ?? orgTimeZone();
  try {
    return d.toLocaleTimeString(LOCALE, {
      hour: '2-digit', minute: '2-digit', hour12: true,
      ...(zone ? { timeZone: zone } : {}),
    }).toLowerCase();
  } catch {
    return d.toLocaleTimeString(LOCALE, {
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).toLowerCase();
  }
}

/** "22/09/2026 02:30 pm" — the date in the account's pattern, the time in the
 *  organisation's zone. */
export function displayDateTime(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  return `${displayDate(value, o)} ${displayTime(value, o)}`;
}


// ---------------------------------------------------------------------------
// Values, which are not display
// ---------------------------------------------------------------------------

/**
 * 🔴 `yyyy-MM-dd` FOR A VALUE, NEVER FOR A READER. An <input type="date">, a
 * query parameter and a request body all need the ISO form whatever the person
 * has chosen to look at — a form that posted 09/22/2026 because somebody
 * preferred American order would be broken, not localised.
 *
 * It is here, rather than left as a bare date-fns `format` call, so the guard
 * can forbid that call everywhere: "is this a value or a date on screen" is
 * the one question a reader of those call sites could not answer, and the
 * answer now has a name.
 *
 * Computed from the parts in the ORGANISATION's zone, for the same reason
 * everything else here is: `toISOString().slice(0, 10)` is the UTC day, which
 * is the previous one for half the world for part of every day.
 */
export function isoDate(value: DateValue, o?: { timeZone?: string }): string {
  const d = asDate(value);
  if (!d) return '';
  const { y, m, d: day } = partsIn(d, isPlainDate(value) ? undefined : (o?.timeZone ?? orgTimeZone()));
  return `${y}-${pad(m)}-${pad(day)}`;
}

/** "14:30" — a 24-hour clock, for the places that show a schedule rather than
 *  a moment. The zone rule is the same as everywhere else here. */
export function displayClock(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const zone = o?.timeZone ?? orgTimeZone();
  try {
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      ...(o?.seconds ? { second: '2-digit' } : {}),
      ...(zone ? { timeZone: zone } : {}),
    });
  } catch {
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      ...(o?.seconds ? { second: '2-digit' } : {}),
    });
  }
}


/** "Sep" — a bare month, for a chart axis. */
export function displayMonth(value: DateValue, o?: DisplayOptions): string {
  const d = asDate(value);
  if (!d) return o?.fallback ?? '—';
  const { m } = partsIn(d, zoneFor(value, o));
  return (o?.pattern ?? userDatePattern()) === 'YYYY-MM-DD' ? pad(m) : MONTHS[m - 1]!;
}

// ---------------------------------------------------------------------------
// One name per SHAPE, and the dispatcher the call sites use
// ---------------------------------------------------------------------------

/**
 * 🔴 A SHAPE, NOT A PATTERN. Every call site used to spell its own date-fns
 * pattern — 'd MMM yyyy', 'MMM d, yyyy', 'dd MMM yyyy' were all in use for the
 * same thing — so the app rendered one kind of date three ways and no
 * preference could reach any of them.
 *
 * A shape says what the date is FOR. How it is spelled is then one decision,
 * taken here, against the account's pattern.
 */
export type DateShape =
  | 'date'        // 22/09/2026
  | 'dateLong'    // 22 Sep 2026
  | 'dayMonth'    // 22 Sep
  | 'dayDate'     // Mon, 22 Sep
  | 'full'        // Monday, 22 September 2026
  | 'monthYear'   // Sep 2026
  | 'month'       // Sep
  | 'time'        // 02:30 pm
  | 'clock'       // 14:30
  | 'dateTime';   // 22/09/2026 02:30 pm

const SHAPES: Record<DateShape, (v: DateValue, o?: DisplayOptions) => string> = {
  date: displayDate,
  dateLong: displayDateLong,
  dayMonth: displayDayMonth,
  dayDate: displayDayDate,
  full: displayFull,
  monthYear: displayMonthYear,
  month: displayMonth,
  time: displayTime,
  clock: displayClock,
  dateTime: displayDateTime,
};

export function display(value: DateValue, shape: DateShape, o?: DisplayOptions): string {
  return SHAPES[shape](value, o);
}
